import { getUserSession } from '@/service/UserService.js'
import { useKinectinStore } from '@/store/kinectin.js'
import { storeToRefs } from 'pinia'
import { linkIdToId } from '@/service/uiUtils.js'
import { DateTime } from 'luxon'
import { size } from 'lodash-es'

const unauthenticatedRoutes = [
    'signin',
    'signup',
    'signupwith-orgUrlSegment',
    'signinwith-orgUrlSegment',
    'privacy',
    'terms',
    'about',
    'auth_callback',
    'berightback',
]
const webAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]
const nativeAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]

function setupRouterMiddleware({ router }) {
    router.beforeEach(async (to, from, next) => {
        if (['berightback', 'privacy', 'terms', 'about', 'auth_callback'].includes(to.name)) return next()

        const store = useKinectinStore()
        const { maintenanceMode, userIsAuthenticated, userProfile } = storeToRefs(store)

        if (maintenanceMode.value) return next({ path: '/berightback', query: { redirect: to.fullPath } })

        if (to.name === 'signinwith-orgUrlSegment') { // special handling here
            const { token } = await getUserSession()
            if (token) return next(to.query.redirect)
        }

        const { token } = await getUserSession()
        if (token) userIsAuthenticated.value = true

        let title = to.path === '/' ? 'Kinectin' : `Kinectin - ${to.path}`
        const baselineOnboarding = to.name === 'assessments-baseline' && to.query.onboarding
        if (unauthenticatedRoutes.includes(to.name) || baselineOnboarding) {
            document.title = title
            return next()
        }
        if (!token) {
            await store.clearCoachGreet() // in case a different user logs in
            if (['signupwith-orgUrlSegment', 'signinwith-orgUrlSegment'].includes(to.name)) {
                return next()
            }
            return next({ path: '/signup', query: { redirect: to.fullPath } })
        }
        await store.loadUserProfile()

        if (
            !userProfile.value.onboarded &&
            !['assessments-baseline', 'auth_callback', 'billing', 'onboard'].includes(to.name)
        ) {
            return next({ path: '/auth_callback' })
        }

        const fnsByRouteName = {
            'orgs': store.userCanManageOrgIds,
            'org-orgId': store.userCanManageOrgIds,
        }
        const fn = fnsByRouteName[to.name]
        if (fn && !fn()) return next({ path: '/chat/new' })

        // if the user is in range of a required screener, redirect to the screener if they haven't taken it yet
        const orgId = userProfile.value.orgId
        if (orgId && userProfile.value.onboarded) {
            const org = await store.getOrgPublicProfile({ orgId })
            const screenerId = org.requiredScreenerId
            if (screenerId &&
                !(to.name === 'screeners-screener' && to.query.screenerId === screenerId) &&
                org.requiredScreenerStartDate &&
                org.requiredScreenerEndDate)
            {
                const now = DateTime.now()
                const startDate = DateTime.fromFormat(org.requiredScreenerStartDate, 'yyyy-MM-dd')
                const endDate = DateTime.fromFormat(org.requiredScreenerEndDate, 'yyyy-MM-dd')
                if (startDate <= now && now < endDate) {
                    const screener = await store.getOrgScreener({ orgId, screenerId })
                    const questionsSize = size(screener.formConfig.questions)
                    let taken = false
                    await store.loadRecentScreenerAnswers({ orgId, screenerId })
                    const results = await store.getRecentScreenerAnswers({ orgId, screenerId })
                    for (const result of (results || []).filter(e => e.timeFragment)) {
                        let timeFragment = DateTime.fromFormat(result.timeFragment, 'yyyy-MM-dd')
                        if (!timeFragment.isValid) {
                            timeFragment = DateTime.fromFormat(result.timeFragment, 'yyyy-MM')
                            if (timeFragment.year === now.year && timeFragment.month === now.month) {
                                timeFragment = timeFragment.set({ day: now.day })
                            }
                        }
                        if (timeFragment && startDate <= timeFragment && timeFragment < endDate) {
                            taken = questionsSize === size(result.answers)
                            if (taken) break
                        }
                    }
                    if (!taken) {
                        return next({ path: '/screeners/screener', query: { orgId, screenerId, required: 1 } })
                    }
                }
            }
        }

        if (to.name === 'chat-threadId' && to.path !== '/chat/new') {
            const linkId = to.params.threadId
            const threadId = linkIdToId({ linkId })
            await store.selectThread({ threadId })
            const { selectedThread } = storeToRefs(store)
            title = `Kinectin - ${selectedThread.value.name}`
        }
        document.title = title
        return next()
    })
}

export {
    setupRouterMiddleware,
    unauthenticatedRoutes,
    webAppHideDrawerRoutes,
    nativeAppHideDrawerRoutes,
}